import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import StepperBar from '../SetupProgress/StepperBar'
import FixtureService from 'src/service/FixtureService'
import Fixture from './Fixture'
import ManageFixture from './ManageFixture'
import CompletedFixture from './CompletedFixture'
import FixtureSetting from './FixtureSetting'

// Each tab keeps its own route, so every existing link into these screens still
// lands on the right tab and the sidebar keeps highlighting correctly.
const TABS = [
  {
    key: 'fixtures',
    label: 'All Fixtures',
    path: '/create-fixtures',
    countKey: 'total',
    countHint: 'Fixtures scheduled',
  },
  {
    key: 'scores',
    label: 'Manage Scores',
    path: '/manage-scores',
    countKey: 'awaitingSquad',
    countHint: 'Fixtures still awaiting a squad',
  },
  {
    key: 'completed',
    label: 'Completed',
    path: '/completed-fixtures',
    countKey: 'completed',
    countHint: 'Fixtures completed',
  },
  { key: 'settings', label: 'Settings', path: '/fixture-setting' },
]

const FixturesHub = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [counts, setCounts] = useState({})

  const activeTab = useMemo(
    () => TABS.find((tab) => tab.path === location.pathname) ?? TABS[0],
    [location.pathname],
  )

  // Cheap count-only reads (limit 1) so every tab can show its number without
  // waiting for the tab itself to be opened.
  const fetchCounts = useCallback(async () => {
    const params = new URLSearchParams()
    const [total, completed, awaitingSquad] = await Promise.all([
      FixtureService.getFixture(0, 1, 1, params)
        .then((res) => res?.data?.total ?? null)
        .catch(() => null),
      FixtureService.getCompletedFixture(0, 1, 1, params)
        .then((res) => res?.data?.total ?? null)
        .catch(() => null),
      FixtureService.getSquadFixture()
        .then((res) => res?.data?.length ?? null)
        .catch(() => null),
    ])
    setCounts({ total, completed, awaitingSquad })
  }, [])

  // Refresh on every tab change, so a fixture added on one tab is reflected in
  // the others' pills as soon as you move between them.
  useEffect(() => {
    fetchCounts()
  }, [fetchCounts, location.pathname])

  return (
    <>
      <StepperBar activeStepKey="fixtures" />

      <div className="ge-page-header">
        <h1 className="ge-page-title">Fixtures</h1>
        <p className="ge-page-subtitle">
          Schedule matches, enter scores and review completed fixtures.
        </p>
      </div>

      <div className="ge-tabs" role="tablist">
        {TABS.map((tab) => {
          const count = tab.countKey ? counts[tab.countKey] : null
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab.key === tab.key}
              className={`ge-tab${activeTab.key === tab.key ? ' active' : ''}`}
              onClick={() => navigate(tab.path)}
            >
              {tab.label}
              {count !== null && count !== undefined && (
                <span className="ge-tab-count" title={tab.countHint}>
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {activeTab.key === 'fixtures' && <Fixture />}
      {activeTab.key === 'scores' && <ManageFixture />}
      {activeTab.key === 'completed' && <CompletedFixture />}
      {activeTab.key === 'settings' && <FixtureSetting />}
    </>
  )
}

export default FixturesHub
